(() => {
  const nativeShippingReadyClass = "cart-rewards-native-shipping-ready";
  const campaignNode = document.getElementById("CartRewardsCampaign");
  const bootstrap = document.querySelector(
    '[data-cart-rewards-bootstrap][data-cart-rewards-source="standalone"]',
  );
  if (!campaignNode || !bootstrap) {
    document.documentElement.classList.add(nativeShippingReadyClass);
    return;
  }

  const parseJson = (node, fallback) => {
    try {
      return JSON.parse(node?.textContent || "") ?? fallback;
    } catch {
      return fallback;
    }
  };

  const campaign = parseJson(campaignNode, null);
  const initialProducts = parseJson(
    document.getElementById("CartRewardsInitialProducts"),
    {},
  );
  const initialCart = parseJson(
    document.getElementById("CartRewardsInitialCart"),
    null,
  );
  if (!campaign?.enabled || campaign.currency !== "USD") {
    document.documentElement.classList.add(nativeShippingReadyClass);
    return;
  }

  const productEligibility = new Map(
    Object.entries(initialProducts).map(([handle, value]) => [
      handle,
      Boolean(value.defaultFreeShipping || value.hasFallbackTag),
    ]),
  );
  const defaultProductTag = campaign.freeShipping?.defaultProductTag;
  const storefrontLocale = "en-US";
  const configuredAccent = String(bootstrap.dataset.accentColor || "#c20c9f");
  const accentColor = configuredAccent;
  const hideNativeShipping = bootstrap.dataset.hideNativeShipping !== "false";
  const nativeShippingSelectors = {
    drawer: bootstrap.dataset.drawerNativeShippingSelector,
    page: bootstrap.dataset.pageNativeShippingSelector,
  };
  const nativeHiddenClass = "cart-rewards-native-shipping--hidden";
  const root = window.Shopify?.routes?.root || "/";
  const refreshDebounceMs = 150;
  const refreshSettleDelayMs = 500;
  const refreshRetryBaseMs = 500;
  const refreshRetryMaxMs = 30_000;
  const refreshRetryJitterRatio = 0.2;
  let refreshTimer = null;
  let refreshInFlight = false;
  let refreshRequested = false;
  let refreshRetryAttempt = 0;
  let refreshNotBefore = 0;
  let rateLimitAttempt = 0;
  let cartMutationSequence = 0;
  let lastValidCart = null;
  let lastGiftResult = null;
  let mutationResponsePending = 0;
  const rewardHistory = new Map();
  const nativeShippingGuardLocations = new Set();

  const money = (cents) =>
    new Intl.NumberFormat(storefrontLocale, {
      style: "currency",
      currency: "USD",
    }).format(Math.max(0, cents) / 100);
  const shippingLookups = new Map();

  const compactMoney = (amount) =>
    new Intl.NumberFormat(storefrontLocale, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Math.max(0, amount));

  const discountValueLabel = (tier) =>
    (tier.type || campaign.discountType) === "percentage"
      ? `${tier.value}%`
      : money(tier.value * 100);

  const discountBenefitLabel = (tier) => tier.label || `${discountValueLabel(tier)} off`;
  const templateMessage = (template, fallback, values) =>
    String(template ?? fallback).replace(/\{(amount|reward)\}/g, (_, key) => values[key] || "");

  async function isDefaultFreeShipping(item) {
    if (productEligibility.has(item.handle)) {
      return productEligibility.get(item.handle);
    }
    if (shippingLookups.has(item.handle)) return shippingLookups.get(item.handle);
    const lookup = (async () => { try {
      const response = await fetch(`${root}products/${item.handle}.js`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error("Product lookup failed");
      const product = await response.json();
      const eligible =
        Array.isArray(product.tags) && product.tags.includes(defaultProductTag);
      productEligibility.set(item.handle, eligible);
      return eligible;
    } catch {
      return null;
    } })();
    shippingLookups.set(item.handle, lookup);
    try { return await lookup; } finally { shippingLookups.delete(item.handle); }
  }

  async function shippingState(cart, thresholdCents) {
    if (campaign.freeShipping.enabled === false) {
      return { status: "disabled", unlocked: false, message: "" };
    }
    const shippableItems = cart.items.filter(
      (item) => item.requires_shipping !== false,
    );
    if (!shippableItems.length) {
      return {
        status: "not-required",
        unlocked: false,
        message: "No shipping required.",
      };
    }
    const defaultFlags = await Promise.all(
      shippableItems.map((item) => isDefaultFreeShipping(item)),
    );
    if (defaultFlags.some((flag) => flag === null)) {
      return {
        status: "unknown",
        unlocked: false,
        message: "Shipping eligibility unavailable. Please retry.",
      };
    }
    const allDefault = defaultFlags.every(Boolean);
    const eligibleSubtotalCents = shippableItems.reduce(
      (sum, item, index) =>
        defaultFlags[index] ? sum : sum + Number(item.final_line_price || 0),
      0,
    );
    const unlocked = allDefault || eligibleSubtotalCents >= thresholdCents;
    return {
      status: unlocked ? "unlocked" : "locked",
      unlocked,
      eligibleSubtotalCents,
      message: unlocked
        ? templateMessage(campaign.freeShipping.unlocked, "Free shipping unlocked.", {})
        : templateMessage(campaign.freeShipping.pending,
            "Add {amount} in eligible items for free shipping.",
            { amount: money(thresholdCents - eligibleSubtotalCents) }),
    };
  }

  function rewardPosition(threshold, highestThreshold) {
    return highestThreshold > 0
      ? Math.min(100, Math.max(0, (threshold / highestThreshold) * 100))
      : 0;
  }

  function calculateProgress(subtotal, rewardThresholds) {
    return rewardPosition(subtotal, rewardThresholds.at(-1));
  }

  function giftMilestones(tiers, subtotal, strings) {
    const giftTiers = tiers.filter((tier, index) => tier.giftCount > 0 && (!index || tier.giftCount > tiers[index - 1].giftCount));
    const allDiscounts = tiers.filter((tier, index) => tier.value > 0 &&
      (index === 0 || tier.value !== tiers[index - 1].value || tier.type !== tiers[index - 1].type));
    const windowOf = (list, limit) => {
      const next = list.findIndex(tier => tier.threshold > subtotal);
      const start = Math.min(Math.max(0, (next < 0 ? list.length : next) - 1), Math.max(0, list.length - limit));
      return list.slice(start, start + limit);
    };
    const discounts = windowOf(allDiscounts, giftTiers.length ? 3 : 4);
    if (!discounts.length || giftTiers[0]?.threshold < discounts[0]?.threshold) {
      const visible = windowOf(!discounts.length ? giftTiers : tiers.filter((tier, index) => !index ||
        tier.giftCount !== tiers[index - 1].giftCount || tier.value !== tiers[index - 1].value || tier.type !== tiers[index - 1].type), 4);
      const step = 100 / Math.max(1, visible.length);
      const next = visible.findIndex(tier => tier.threshold > subtotal);
      const index = next < 0 ? visible.length : next;
      const previous = visible[index - 1]?.threshold || 0;
      const progress = next < 0 ? 100 : (index + (subtotal - previous) / (visible[index].threshold - previous)) * step;
      return { nodes: visible.map((tier, i) => ({key:`gift-tier-${tier.threshold}`,threshold:tier.threshold,
        benefit:tier.label,displayBenefit:tier.nodeLabel,unlocked:subtotal>=tier.threshold,position:(i+1)*step})),
        discounts, progress:Math.max(0,Math.min(100,progress)) };
    }
    const lastDiscountThreshold = discounts.at(-1)?.threshold || 0;
    const laterGifts = giftTiers.filter((tier) => tier.threshold > lastDiscountThreshold);
    const nextGift = laterGifts.find((tier) => subtotal < tier.threshold);
    const gift = nextGift || laterGifts.at(-1);
    const nodes = discounts.map((tier) => ({
      key: `discount-${tier.threshold}-${tier.value}`,
      threshold: tier.threshold,
      benefit: tier.label || tier.discountLabel,
      displayBenefit: tier.nodeLabel || discountValueLabel(tier),
      unlocked: subtotal >= tier.threshold,
    }));
    if (gift) {
      const remaining = money((gift.threshold - subtotal) * 100);
      nodes.push({
        key: `gift-${gift.giftCount}`,
        threshold: gift.threshold,
        displayThreshold: nextGift ? remaining : strings.node_all_gifts || "All gifts",
        benefit: nextGift ? strings.node_next_gift || "Next gift" : strings.node_gifts_unlocked || "Unlocked",
        ariaLabel: nextGift
          ? templateMessage(strings.node_next_gift_aria, "Add {amount} more in eligible items for your next gift.", { amount: remaining })
          : strings.node_all_gifts_aria || "All gift tiers unlocked.",
        unlocked: !nextGift,
      });
    }
    const step = 100 / nodes.length;
    nodes.forEach((node, index) => { node.position = (index + 1) * step; });
    let progress = 100;
    for (let index = 0; index < discounts.length; index++) {
      const threshold = discounts[index].threshold;
      if (subtotal < threshold) {
        const previous = discounts[index - 1]?.threshold || 0;
        progress = (index + (subtotal - previous) / (threshold - previous)) * step;
        break;
      }
    }
    if (nextGift && subtotal >= (discounts.at(-1)?.threshold || 0)) {
      const previous = Math.max(discounts.at(-1)?.threshold || 0,
        giftTiers.filter((tier) => tier.threshold <= subtotal).at(-1)?.threshold || 0);
      progress = discounts.length * step + (subtotal - previous) / (nextGift.threshold - previous) * step;
    }
    return { nodes, discounts, progress: Math.min(100, Math.max(0, progress)) };
  }

  async function calculateState(cart, giftResult) {
    const gifts = giftResult || (typeof window !== "undefined" && window.LRBlackFridayGifts
      ? await window.LRBlackFridayGifts.prepare(cart) : null);
    for (const item of gifts?.data.items || []) {
      if (item.handle && typeof item.defaultFreeShipping === "boolean") {
        productEligibility.set(item.handle, item.defaultFreeShipping);
      }
    }
    const rewardUi = gifts?.enabled || gifts?.uiEnabled;
    const subtotal = rewardUi ? gifts.state.subtotalCents / 100
      : Number(cart[campaign.amountBasis || "items_subtotal_price"] || 0) / 100;
    const shipping = await shippingState(
      cart,
      campaign.freeShipping.threshold * 100,
    );
    // Display only: Shopify's existing automatic discounts remain authoritative.
    const giftTiers = rewardUi ? (gifts.tiers || gifts.data.rewardTiers)
      .map((tier) => {
        const threshold = tier.thresholdCents / 100;
        const reward = tier;
        const giftLabel = window.LRBlackFridayGifts.format(
          (reward.giftCount === 1 ? gifts.data.strings.reward_one : gifts.data.strings.reward_other) || gifts.data.strings.reward,
          { count: reward.giftCount });
        const giftNodeLabel = window.LRBlackFridayGifts.format(
          (reward.giftCount === 1 ? gifts.data.strings.node_one : gifts.data.strings.node_other) || gifts.data.strings.reward,
          { count: reward.giftCount });
        const type = reward.discountType || 'percentage';
        const value = reward.discountValue ?? reward.discountPercent;
        const discountLabel = value > 0 ? type === 'fixed_amount' ? `${money(value * 100)} off`
          : window.LRBlackFridayGifts.format(gifts.data.strings.discount, { percent: value }) : '';
        const preview = value > 0 && (gifts.enabled || gifts.data.requested) && gifts.data.backendMode === 'test_codes' ? ` (${gifts.data.strings.preview_label})` : '';
        return { threshold, value, type, giftCount: reward.giftCount,
          discountLabel: `${discountLabel}${gifts.data.backendMode === "test_codes" ? ` (${gifts.data.strings.preview_label})` : ''}`,
          nodeLabel: `${value > 0 ? type === 'fixed_amount' ? `${money(value*100)} off` : `${value}% off` : ''}${reward.giftCount ? `${value > 0 ? '\n' : ''}${giftNodeLabel}` : ''}`,
          label: reward.label || `${discountLabel}${preview}${reward.giftCount ? `${value > 0 ? ' + ' : ''}${giftLabel}` : ''}` };
      }) : null;
    const tiers = [...(giftTiers || campaign.discountTiers)].sort(
      (left, right) => left.threshold - right.threshold,
    );
    const milestones = rewardUi ? giftMilestones(tiers, subtotal, gifts.data.strings) : null;
    const messageTiers = tiers;
    const nextTier = messageTiers.find((tier) => subtotal < tier.threshold);
    let message = nextTier
      ? templateMessage(rewardUi ? gifts.data.strings.pending : campaign.messages?.pending, "Add {amount} more to unlock {reward}.",
          { amount: money((nextTier.threshold - subtotal) * 100), reward: discountBenefitLabel(nextTier) })
      : messageTiers.length
        ? templateMessage(rewardUi ? gifts.data.strings.unlocked : campaign.messages?.unlocked, "{reward} unlocked!",
            { reward: discountBenefitLabel(messageTiers.at(-1)) })
        : "";
    if (campaign.freeShipping.showNode && campaign.freeShipping.enabled && !shipping.unlocked) {
      message = shipping.message;
    }
    const progress = milestones?.progress ?? calculateProgress(
      subtotal,
      [...(campaign.freeShipping.showNode && campaign.freeShipping.enabled
        ? [campaign.freeShipping.threshold] : []), ...tiers.map((tier) => tier.threshold)],
    );
    return {
      message,
      messageReward: nextTier ? discountBenefitLabel(nextTier) : messageTiers.length ? discountBenefitLabel(messageTiers.at(-1)) : '',
      ariaValueText: message,
      subtotal,
      shipping,
      shippingUnlocked: shipping.unlocked,
      tiers,
      progress,
      gifts,
      milestoneNodes: milestones?.nodes,
    };
  }

  function rewardNodes(state) {
    if (state.milestoneNodes) return state.milestoneNodes;
    return [
      ...(campaign.freeShipping.showNode && campaign.freeShipping.enabled && state.tiers.length < 4
        ? [{ key: "shipping", threshold: campaign.freeShipping.threshold,
             benefit: campaign.freeShipping.label || "Free Shipping",
             unlocked: state.shippingUnlocked }] : []),
      ...state.tiers.slice(0, 4).map((tier) => ({
      key: `discount-${tier.threshold}-${tier.value}`,
      threshold: tier.threshold,
      benefit: discountBenefitLabel(tier),
      displayBenefit: tier.nodeLabel || discountBenefitLabel(tier),
      unlocked: state.subtotal >= tier.threshold,
      })),
    ];
  }

  function messageMarkup(message) {
    const escaped = String(message).replace(
      /[&<>"']/g,
      (char) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[char],
    );
    return escaped.replace(
      /\$[\d,]+(?:\.\d{2})?/g,
      (amount) => `<strong class="cart-rewards__amount">${amount}</strong>`,
    );
  }

  const escapeText = (value) => String(value).replace(/[&<>"']/g,
    (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]);


  function milestoneGeometry(nodes, progress) {
    const gap = 100 / Math.max(4, nodes.length);
    let previous = 0;
    const positioned = nodes.map((node, index) => {
      const position = Math.min(100 - (nodes.length - index - 1) * gap,
        Math.max(previous + gap, rewardPosition(node.threshold, nodes.at(-1).threshold)));
      previous = position;
      return { ...node, position };
    });
    const logical = Math.min(100, Math.max(0, progress));
    const step = 100 / nodes.length;
    const index = Math.min(nodes.length - 1, Math.floor(logical / step));
    const start = positioned[index - 1]?.position || 0;
    return { nodes: positioned, progress: start + (positioned[index].position - start) *
      (logical - index * step) / step };
  }

  function renderHost(host, state) {
    const originalNodes = rewardNodes(state);
    if (!originalNodes.length) { host.hidden = true; return; }
    const unified = Boolean(state.gifts?.enabled || state.gifts?.uiEnabled);
    const geometry = unified ? milestoneGeometry(originalNodes, state.progress)
      : { nodes: originalNodes, progress: state.progress };
    const nodes = geometry.nodes;
    if (!nodes.length) { host.hidden = true; return; }
    const nextIndex = nodes.findIndex((node) => !node.unlocked);
    const location = host.dataset.cartRewards;
    const history = rewardHistory.get(location);
    const previousUnlocked = new Set(
      String(history?.unlocked || host.dataset.cartRewardsUnlocked || "")
        .split(",")
        .filter(Boolean),
    );
    const hasRendered =
      Boolean(history) || host.dataset.cartRewardsInitialized === "true";
    const signature = `${Math.round(state.subtotal * 100)}:${state.shippingUnlocked}:${nodes
      .map((node) => Number(node.unlocked))
      .join("")}`;
    const stateChanged =
      (history?.signature || host.dataset.cartRewardsSignature) !== signature;
    const renderedNodes = nodes.map((node, index) => ({
      ...node,
      next: index === nextIndex,
      justUnlocked:
        hasRendered &&
        stateChanged &&
        node.unlocked &&
        !previousUnlocked.has(node.key),
    }));
    const hasNewUnlock = renderedNodes.some((node) => node.justUnlocked);

    host.hidden = false;
    host.dataset.cartRewardsGiftUi = String(unified);
    host.dataset.cartRewardsMotion = String(campaign.motion !== false);
    host.style.setProperty("--cart-rewards-accent", accentColor);
    host.style.setProperty('--cart-rewards-node-count', nodes.length);
    const visualProgress = geometry.progress;
    const rehydrating = host.dataset.cartRewardsInitialized !== 'true' &&
      Number.isFinite(history?.progress) && campaign.motion !== false;
    host.style.setProperty("--cart-rewards-progress", `${visualProgress}%`);
    host.style.setProperty('--cart-rewards-progress-scale', String((rehydrating ? history.progress : visualProgress) / 100));
    if (rehydrating) requestAnimationFrame(() => requestAnimationFrame(() => {
      if (host.isConnected && host.dataset.cartRewardsSignature === signature) {
        host.style.setProperty('--cart-rewards-progress-scale', String(visualProgress / 100));
      }
    }));
    const progressbar = host.querySelector('[role="progressbar"]');
    progressbar?.setAttribute(
      "aria-valuenow",
      String(Math.floor(visualProgress)),
    );
    progressbar?.setAttribute("aria-valuetext", state.ariaValueText);
    const shippingNode = host.querySelector("[data-cart-rewards-shipping]");
    if (shippingNode) {
      if (state.shipping.status === "locked" && (state.gifts?.enabled || state.gifts?.uiEnabled)) {
        shippingNode.innerHTML = messageMarkup(state.shipping.message);
      } else {
        shippingNode.textContent = state.shipping.unlocked && (state.gifts?.enabled || state.gifts?.uiEnabled)
          ? state.gifts.data.strings.shipping_badge || "Free shipping" : state.shipping.message;
      }
      shippingNode.dataset.shippingStatus = state.shipping.status;
      shippingNode.hidden = state.shipping.status === "disabled";
    }
    const retry = host.querySelector("[data-cart-rewards-retry]");
    if (retry) retry.hidden = state.shipping.status !== "unknown";
    const messageNode = host.querySelector("[data-cart-rewards-message]");
    messageNode.innerHTML = (state.gifts?.enabled || state.gifts?.uiEnabled) && state.messageReward
      ? messageMarkup(state.message).replace(escapeText(state.messageReward), `<strong class="cart-rewards__reward">${escapeText(state.messageReward)}</strong>`)
      : messageMarkup(state.message);
    const badges = host.querySelector('[data-cart-rewards-benefits]');
    if (badges) {
      const currentTier = state.tiers.filter((tier) => state.subtotal >= tier.threshold).at(-1);
      const labels = (state.gifts?.enabled || state.gifts?.uiEnabled) && currentTier ? [
        ...(currentTier.value > 0 ? [currentTier.type === 'fixed_amount' ? `${money(currentTier.value*100)} off`
          : window.LRBlackFridayGifts.format(state.gifts.data.strings.discount, {percent:currentTier.value})] : []),
        ...(currentTier.giftCount ? [window.LRBlackFridayGifts.format(currentTier.giftCount === 1
          ? state.gifts.data.strings.reward_one : state.gifts.data.strings.reward_other, {count:currentTier.giftCount})] : [])
      ] : [];
      badges.innerHTML = labels.map((label) => `<span class="cart-rewards__badge">${escapeText(label)}</span>`).join('');
      badges.hidden = !labels.length;
    }
    const statusNode = host.querySelector("[data-cart-rewards-status]");
    host.dataset.cartRewardsReadyStatus = state.gifts?.enabled || state.gifts?.uiEnabled ? '' : "Discounts confirmed at checkout.";
    if (statusNode && !refreshInFlight && Date.now() >= refreshNotBefore) {
      statusNode.textContent = host.dataset.cartRewardsReadyStatus;
      statusNode.hidden = !statusNode.textContent;
    }
    messageNode.hidden = !state.message;
    const nodesElement = host.querySelector("[data-cart-rewards-nodes]");
    nodesElement.style.setProperty("--cart-rewards-node-count", nodes.length);
    nodesElement.innerHTML = nodes
      .map((node, index) => {
        const renderedNode = renderedNodes[index];
        const stateClass = node.unlocked
          ? "is-unlocked"
          : renderedNode.next
            ? "is-next"
            : "is-locked";
        const motionClasses = [
          campaign.motion !== false && renderedNode.next &&
          stateChanged &&
          (hasRendered || host.getClientRects().length > 0)
            ? "is-next-reminding"
            : "",
          campaign.motion !== false && renderedNode.justUnlocked ? "is-just-unlocked" : "",
        ]
          .filter(Boolean)
          .join(" ");
        const threshold = node.displayThreshold || compactMoney(node.threshold);
        const ariaState = node.unlocked
          ? "unlocked"
          : renderedNode.next
            ? "next reward"
            : "locked";
        const position = node.position ?? rewardPosition(node.threshold, nodes.at(-1).threshold);
        const ariaLabel = node.ariaLabel || `${threshold} ${node.benefit}, ${ariaState}`;
        const benefit = String(node.displayBenefit || node.benefit).split('\n');
        const benefitMarkup = (state.gifts?.enabled || state.gifts?.uiEnabled) && node.key.startsWith('discount-')
          ? `<span>${escapeText(benefit[0])}</span>${benefit[1] ? `<em>+${escapeText(benefit[1])}</em>` : ''}`
          : escapeText(node.displayBenefit || node.benefit);
        return `<li class="cart-rewards__node ${stateClass}${motionClasses ? ` ${motionClasses}` : ""}" style="--cart-rewards-node-position: ${position}%" data-reward-key="${node.key}" aria-label="${escapeText(ariaLabel)}"${renderedNode.next ? ' aria-current="step"' : ""}>
          <span class="cart-rewards__dot" aria-hidden="true">${node.unlocked ? '<svg width="11" height="11" viewBox="0 0 12 12"><path d="m2 6 2.5 2.5L10 3" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>' : ""}</span>
          <span class="cart-rewards__label">
            <span class="cart-rewards__threshold">${escapeText(threshold)}</span>
            <span class="cart-rewards__benefit">${benefitMarkup}</span>
          </span>
        </li>`;
      })
      .join("");
    host.classList.toggle("is-celebrating", campaign.motion !== false && hasNewUnlock);
    host.dataset.cartRewardsInitialized = "true";
    host.dataset.cartRewardsSignature = signature;
    const unlockedKeys = renderedNodes
      .filter((node) => node.unlocked)
      .map((node) => node.key)
      .join(",");
    host.dataset.cartRewardsUnlocked = unlockedKeys;
    rewardHistory.set(location, { signature, unlocked: unlockedKeys, progress: visualProgress });
  }

  function prepareHost(host, location) {
    const wasPrepared = host.dataset.cartRewardsHostReady === "true";
    host.className = `cart-rewards cart-rewards--${location}`;
    host.dataset.cartRewards = location;
    host.style.setProperty("--cart-rewards-accent", accentColor);
    host.setAttribute("aria-label", "Cart rewards progress");
    if (!host.querySelector('[role="progressbar"]')) {
      host.innerHTML = `
        <div class="cart-rewards__panel">
        <p class="cart-rewards__message" data-cart-rewards-message aria-live="polite" hidden></p>
        <div class="cart-rewards__track" role="progressbar" aria-valuemin="0" aria-valuemax="100">
          <span class="cart-rewards__fill"></span>
        </div>
        <ol class="cart-rewards__nodes" data-cart-rewards-nodes></ol>
        <div class="cart-rewards__unlocked">
          <p class="cart-rewards__shipping" data-cart-rewards-shipping aria-live="polite"></p>
          <div data-cart-rewards-benefits hidden></div>
        </div>
        <div class="cart-rewards__feedback">
          <span data-cart-rewards-status role="status">Discounts confirmed at checkout.</span>
          <button type="button" data-cart-rewards-retry hidden>Retry</button>
        </div><span class="cart-rewards__sr-status" data-cart-rewards-updating role="status" aria-live="polite"></span></div>`;
      host
        .querySelector("[data-cart-rewards-retry]")
        ?.addEventListener("click", () => {
          resetRefreshBackoff();
          requestRefresh({ delay: 0, overrideBackoff: true });
        });
    }
    host.dataset.cartRewardsHostReady = "true";
    if (location === "drawer") bindDrawerOpenReminder(host);
    return !wasPrepared;
  }

  function createHost(location) {
    const host = document.createElement("section");
    prepareHost(host, location);
    return host;
  }

  function remindNextReward(host) {
    if (campaign.motion === false) return;
    const nextReward = host.querySelector(".cart-rewards__node.is-next");
    if (!nextReward) return;
    nextReward.classList.remove("is-next-reminding");
    requestAnimationFrame(() => nextReward.classList.add("is-next-reminding"));
  }

  function bindDrawerOpenReminder(host) {
    const drawerDetails = host.closest("details");
    if (
      !drawerDetails ||
      drawerDetails.dataset.cartRewardsReminderBound === "true"
    ) {
      return;
    }
    drawerDetails.dataset.cartRewardsReminderBound = "true";
    drawerDetails.addEventListener("toggle", () => {
      const currentHost = drawerDetails.querySelector(
        '[data-cart-rewards="drawer"]',
      );
      if (
        !drawerDetails.open ||
        !currentHost ||
        currentHost.dataset.cartRewardsOpenReminded === "true"
      ) {
        return;
      }
      currentHost.dataset.cartRewardsOpenReminded = "true";
      remindNextReward(currentHost);
    });
  }

  function nativeShippingNodes(location) {
    const selector = nativeShippingSelectors[location];
    if (!selector) return [];
    try {
      return [...document.querySelectorAll(selector)];
    } catch (error) {
      console.warn("Cart Rewards native shipping selector is invalid.", error);
      return [];
    }
  }

  function setNativeShippingHidden(location, shouldHide) {
    nativeShippingNodes(location).forEach((node) => {
      if (hideNativeShipping && shouldHide) {
        node.classList.add(nativeHiddenClass);
        node.dataset.cartRewardsNativeHidden = "true";
        node.setAttribute("aria-hidden", "true");
        return;
      }
      if (node.dataset.cartRewardsNativeHidden === "true") {
        node.classList.remove(nativeHiddenClass);
        delete node.dataset.cartRewardsNativeHidden;
        node.removeAttribute("aria-hidden");
      }
    });
  }

  function syncNativeShippingVisibility(cartHasItems) {
    ["drawer", "page"].forEach((location) => {
      const host = document.querySelector(`[data-cart-rewards="${location}"]`);
      const shouldHide = Boolean(cartHasItems && host && !host.hidden);
      if (hideNativeShipping && shouldHide) {
        nativeShippingGuardLocations.add(location);
      } else {
        nativeShippingGuardLocations.delete(location);
      }
      setNativeShippingHidden(location, shouldHide);
    });
  }

  function applyNativeShippingGuard() {
    nativeShippingGuardLocations.forEach((location) =>
      setNativeShippingHidden(location, true),
    );
  }

  function restoreNativeShipping() {
    nativeShippingGuardLocations.clear();
    ["drawer", "page"].forEach((location) =>
      setNativeShippingHidden(location, false),
    );
  }

  function ensureHosts() {
    let changedHost = false;
    const placements = [
      ["drawer", bootstrap.dataset.drawerSelector, "beforebegin"],
      ["page", bootstrap.dataset.pageSelector, "beforebegin"],
    ];
    placements.forEach(([location, selector, position]) => {
      if (!selector) return;
      const existingHost = document.querySelector(
        `[data-cart-rewards="${location}"]`,
      );
      const target = document.querySelector(selector);
      if (existingHost) {
        if (target && target.previousElementSibling !== existingHost) {
          target.insertAdjacentElement(position, existingHost);
          changedHost = true;
        }
        changedHost = prepareHost(existingHost, location) || changedHost;
        return;
      }
      if (!target) return;
      const host = createHost(location);
      target.insertAdjacentElement(position, host);
      if (location === "drawer") bindDrawerOpenReminder(host);
      changedHost = true;
    });
    return changedHost;
  }

  function isCartPayload(cart) {
    return Boolean(
      cart &&
      Array.isArray(cart.items) &&
      Number.isFinite(Number(cart.item_count)) &&
      Number.isFinite(Number(cart.items_subtotal_price)),
    );
  }

  async function renderCart(cart, { remember = true, giftResult } = {}) {
    if (!isCartPayload(cart)) return false;
    ensureHosts();
    const hosts = document.querySelectorAll("[data-cart-rewards]");
    if (cart.item_count === 0) {
      hosts.forEach((host) => (host.hidden = true));
      rewardHistory.clear();
      lastValidCart = null;
      lastGiftResult = null;
      restoreNativeShipping();
      document.documentElement.classList.add(nativeShippingReadyClass);
      return true;
    }
    const renderMutationSequence = cartMutationSequence;
    const state = await calculateState(cart, giftResult);
    if (renderMutationSequence !== cartMutationSequence) return null;
    if (remember) { lastValidCart = cart; lastGiftResult = state.gifts; }
    hosts.forEach((host) => renderHost(host, state));
    if (state.gifts) window.LRBlackFridayGifts.render(state.gifts);
    syncNativeShippingVisibility(true);
    if (hosts.length > 0) {
      document.documentElement.classList.add(nativeShippingReadyClass);
    }
    return true;
  }

  function setRefreshStatus(status) {
    const coolingDown = Date.now() < refreshNotBefore;
    document.querySelectorAll("[data-cart-rewards]").forEach((host) => {
      host.setAttribute("aria-busy", String(status === "updating"));
      const updatingNotice = host.querySelector("[data-cart-rewards-updating]");
      if (updatingNotice) updatingNotice.textContent = status === "updating" && !coolingDown ? "Updating rewards…" : "";
      const node = host.querySelector("[data-cart-rewards-status]");
      if (node) {
        node.textContent = coolingDown
          ? "Rewards updates are temporarily paused. Retrying automatically."
          : status === "error"
            ? "Could not update rewards. Showing the last confirmed totals."
            : host.dataset.cartRewardsReadyStatus ?? "Discounts confirmed at checkout.";
        node.hidden = !node.textContent;
      }
      const retry = host.querySelector("[data-cart-rewards-retry]");
      if (retry) {
        retry.disabled = coolingDown || status === "updating";
        retry.hidden =
          !coolingDown &&
          status !== "error" &&
          host.querySelector("[data-cart-rewards-shipping]")?.dataset
            .shippingStatus !== "unknown";
      }
    });
  }

  function clearRefreshTimer() {
    if (!refreshTimer) return;
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  function resetRefreshBackoff() {
    if (Date.now() < refreshNotBefore) return;
    refreshRetryAttempt = 0;
    clearRefreshTimer();
  }

  function retryAfterMilliseconds(response) {
    const value = response?.headers?.get?.("Retry-After");
    if (!value) return 0;
    const seconds = Number(value);
    if (Number.isFinite(seconds)) {
      return Math.max(seconds * 1000, 0);
    }
    const retryAt = Date.parse(value);
    if (!Number.isFinite(retryAt)) return 0;
    return Math.max(retryAt - Date.now(), 0);
  }

  function nextRetryDelay(retryAfterMs = 0) {
    const exponentialDelay = Math.min(
      refreshRetryBaseMs * 2 ** Math.max(refreshRetryAttempt - 1, 0),
      refreshRetryMaxMs,
    );
    const jitter = Math.round(
      exponentialDelay * refreshRetryJitterRatio * Math.random(),
    );
    return Math.max(retryAfterMs, exponentialDelay + jitter);
  }

  function armRefreshTimer(delay) {
    clearRefreshTimer();
    refreshTimer = setTimeout(
      () => {
        refreshTimer = null;
        void refresh();
      },
      Math.min(Math.max(delay, refreshNotBefore - Date.now()), 2_147_483_647),
    );
  }

  function requestRefresh({
    delay = refreshDebounceMs,
    overrideBackoff = false,
  } = {}) {
    refreshRequested = true;
    applyNativeShippingGuard();
    if (Date.now() < refreshNotBefore) setRefreshStatus("error");
    if (refreshInFlight) return;
    if (refreshTimer && refreshRetryAttempt > 0 && !overrideBackoff) return;
    const nextDelay =
      refreshRetryAttempt > 0 && !overrideBackoff ? nextRetryDelay() : delay;
    armRefreshTimer(nextDelay);
  }

  async function refresh() {
    if (refreshInFlight) {
      refreshRequested = true;
      return;
    }
    if (!refreshRequested) return;
    if (Date.now() < refreshNotBefore) {
      setRefreshStatus("error");
      armRefreshTimer(refreshNotBefore - Date.now());
      return;
    }
    refreshRequested = false;
    refreshInFlight = true;
    setRefreshStatus("updating");
    const mutationSequenceAtStart = cartMutationSequence;
    let failure = null;
    let retryAfterMs = 0;
    try {
      const response = await nativeFetch(`${root}cart.js`, {
        headers: { Accept: "application/json" },
      });
      retryAfterMs = retryAfterMilliseconds(response);
      if (response.status === 429) {
        rateLimitAttempt += 1;
        retryAfterMs = Math.max(
          retryAfterMs,
          Math.min(30_000 * 2 ** Math.min(rateLimitAttempt - 1, 4), 300_000),
        );
      }
      if (!response.ok && retryAfterMs > 0)
        refreshNotBefore = Math.max(
          refreshNotBefore,
          Date.now() + retryAfterMs,
        );
      if (!response.ok) {
        throw new Error(
          `Cart lookup failed with ${response.status || "unknown status"}`,
        );
      }
      const cart = await response.json();
      if (mutationSequenceAtStart !== cartMutationSequence) {
        refreshRequested = true;
        return;
      }
      const rendered = await renderCart(cart);
      if (
        mutationSequenceAtStart !== cartMutationSequence ||
        rendered === null
      ) {
        refreshRequested = true;
        return;
      }
      if (!rendered) throw new Error("Cart lookup returned invalid data");
      refreshRetryAttempt = 0;
      rateLimitAttempt = 0;
      refreshNotBefore = 0;
      setRefreshStatus("ready");
    } catch (error) {
      if (mutationSequenceAtStart !== cartMutationSequence) {
        refreshRequested = true;
        return;
      }
      failure = error;
      setRefreshStatus("error");
      refreshRetryAttempt += 1;
      refreshRequested = true;
      console.warn("Cart Rewards could not refresh.", error);
    } finally {
      refreshInFlight = false;
      if (failure) {
        if (refreshRequested && !refreshTimer) {
          armRefreshTimer(nextRetryDelay(retryAfterMs));
        }
      } else if (refreshRequested && !refreshTimer) {
        requestRefresh();
      }
    }
  }

  const nativeFetch = window.fetch.bind(window);
  const observedMutationVersions = new Map();
  const observedCartKey = (cart) => JSON.stringify([cart.currency, cart.item_count, cart.items_subtotal_price,
    (cart.items || []).map((item) => [item.key, item.quantity, item.original_line_price, item.final_line_price]), cart.discount_codes]);
  function rememberMutation(cart, version) {
    observedMutationVersions.set(observedCartKey(cart), version);
    if (observedMutationVersions.size > 12) observedMutationVersions.delete(observedMutationVersions.keys().next().value);
  }

  async function syncFromCartMutationResponse(response, mutationSequence) {
    try {
      if (!response.ok) throw new Error("Cart mutation failed");
      const payload = await response.json();
      rememberMutation(payload, mutationSequence);
      if (mutationSequence !== cartMutationSequence) return;
      const observed = window.LRBlackFridayGifts?.observeMutationResponse?.(payload);
      const observedCart = observed?.cart || window.LRBlackFridayGifts?.consumeMutationResponse?.(payload);
      const cart = isCartPayload(payload) ? payload : observedCart;
      const rendered = await renderCart(cart, { giftResult: observed?.result });
      if (mutationSequence !== cartMutationSequence || rendered === null)
        return;
      if (rendered) {
        resetRefreshBackoff();
        clearRefreshTimer();
        refreshRequested = false;
        setRefreshStatus("ready");
        return;
      }
    } catch {
      // Fall through to a reconciled cart lookup.
    }
    if (mutationSequence === cartMutationSequence) requestRefresh();
  }

  window.fetch = async (...args) => {
    const url = String(args[0] instanceof Request ? args[0].url : args[0]);
    const isCartMutation =
      /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(url);
    if (isCartMutation) {
      setRefreshStatus("updating");
      applyNativeShippingGuard();
    }
    let mutationSequence = cartMutationSequence;
    const requestArgs = isCartMutation ? window.LRBlackFridayGifts?.bundleMutation?.(args) || args : args;
    const send = () => {
      if (isCartMutation) mutationSequence = ++cartMutationSequence;
      return nativeFetch(...requestArgs);
    };
    try {
      const response = await (isCartMutation && window.LRBlackFridayGifts
        ? window.LRBlackFridayGifts.runCartMutation(send)
        : send());
      if (isCartMutation) {
        let responseForSync = null;
        try {
          responseForSync = response.clone?.();
        } catch {
          // A non-standard fetch wrapper can return an uncloneable response.
        }
        if (responseForSync) {
          mutationResponsePending = mutationSequence;
          queueMicrotask(() => { void syncFromCartMutationResponse(responseForSync, mutationSequence)
            .finally(() => { if (mutationResponsePending === mutationSequence) mutationResponsePending = 0; }); });
        } else {
          requestRefresh();
        }
      }
      return response;
    } catch (error) {
      if (isCartMutation && mutationSequence === cartMutationSequence) {
        requestRefresh();
      }
      throw error;
    }
  };

  document.addEventListener("cart:refresh", () => requestRefresh());
  const renderObservedCart = (cart, giftResult) => {
    const sequence = cartMutationSequence;
    void renderCart(cart, { giftResult }).then((rendered) => {
      if (sequence !== cartMutationSequence) return;
      if (!rendered) { requestRefresh(); return; }
      clearRefreshTimer();
      refreshRequested = false;
      resetRefreshBackoff();
      setRefreshStatus("ready");
    }).catch(() => requestRefresh());
  };
  document.addEventListener("bf-gifts:changed", (event) => {
    cartMutationSequence += 1;
    if (event.detail?.cart && event.detail.result) {
      rememberMutation(event.detail.cart, cartMutationSequence);
      renderObservedCart(event.detail.cart, event.detail.result);
    }
    else requestRefresh();
  });
  document.addEventListener("cart:updated", (event) => {
    let cart = event.detail?.cart;
    if (typeof cart === "string") { try { cart = JSON.parse(cart); } catch { cart = null; } }
    if (isCartPayload(cart)) {
      const observedVersion = observedMutationVersions.get(observedCartKey(cart));
      if (observedVersion !== undefined && observedVersion < cartMutationSequence) return;
      const observed = window.LRBlackFridayGifts?.observeMutationResponse?.(cart);
      renderObservedCart(cart, event.detail?.giftResult || observed?.result);
    } else if (!(mutationResponsePending > 0 && mutationResponsePending === cartMutationSequence)) requestRefresh();
  });
  const observationRoot = document.body || document.documentElement;
  new MutationObserver(() => {
    const changedHost = ensureHosts();
    if (changedHost && lastValidCart && !window.LRBlackFridayGifts?.busy?.() && !mutationResponsePending) {
      void renderCart(lastValidCart, { remember: false, giftResult: lastGiftResult }).then((rendered) => {
        if (rendered === null) requestRefresh();
      }).catch(() => requestRefresh());
    }
    applyNativeShippingGuard();
    if (changedHost && !lastValidCart && !window.LRBlackFridayGifts?.busy?.() && !mutationResponsePending) requestRefresh();
  }).observe(observationRoot, {
    childList: true,
    subtree: true,
  });
  const resumeRefresh = () => {
    resetRefreshBackoff();
    requestRefresh({ delay: 0, overrideBackoff: true });
  };
  window.addEventListener("online", resumeRefresh);
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) resumeRefresh();
  });
  ensureHosts();
  void renderCart(initialCart).catch(() => requestRefresh());
  requestRefresh({ delay: 0 });
})();
